import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface LegalDocumentProps {
  type?: 'privacy' | 'terms';
}

const LegalDocument: React.FC<LegalDocumentProps> = ({ type: propType }) => {
  const navigate = useNavigate();
  const { type: paramType } = useParams<{ type: string }>();
  const type = propType || paramType;
  
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadDocument = async () => {
      setLoading(true);
      setError('');
      
      try {
            const filename = type === 'privacy' ? 'privacy-policy.html' : 'terms-of-service.html';
        const response = await fetch(`/legal/${filename}`);
        
        if (!response.ok) {
          throw new Error('Document not found');
        }
        
        const text = await response.text();
        setContent(text);
      } catch (err) {
        console.error('Error loading legal document:', err);
        setError('Failed to load document. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [type]);

  const title = type === 'privacy' ? 'Privacy Policy' : 'Terms of Service';

  return (
        <Box
          sx={{
            minHeight: '100vh',
            maxHeight: '100vh',
            backgroundColor: '#101216',
            color: '#eceef1',
            overflow: 'hidden',
            py: 4,
          }}
        >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            color: '#3c8cff',
            mb: 3,
            '&:hover': {
              backgroundColor: 'rgba(60, 140, 255, 0.1)',
            },
          }}
        >
          Back to Login
        </Button>

            <Card 
              sx={{ 
                maxHeight: 'calc(100vh - 120px)', 
                overflow: 'auto',
                backgroundColor: '#1a1d23',
                border: '1px solid #40444b',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                // Custom scrollbar styling with gutter
                '&::-webkit-scrollbar': {
                  width: '12px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#2a2d35',
                  borderRadius: '6px',
                  margin: '8px 0',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#40444b',
                  borderRadius: '6px',
                  border: '2px solid #2a2d35',
                  '&:hover': {
                    background: '#4a4e55',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  background: '#2a2d35',
                },
              }}
            >
          <CardContent sx={{ p: 4 }}>
            {/* Remove the duplicate title since the HTML content has its own title */}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Typography
                sx={{
                  color: '#ff6e6e',
                  textAlign: 'center',
                  py: 4,
                }}
              >
                {error}
              </Typography>
            )}

            {!loading && !error && (
              <Box
                sx={{
                  // Reset and base styles
                  '& *': {
                    boxSizing: 'border-box',
                  },
                  '& div, & p, & span, & h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: 0,
                    padding: 0,
                  },
                  
                  // Override all inline text colors for better readability
                  '& span[style*="color"]': {
                    color: '#eceef1 !important',
                  },
                  
                  // Remove any background colors that might cause highlighting
                  '& span[style*="background-color"]': {
                    backgroundColor: 'transparent !important',
                  },
                  
                  // Typography
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    color: '#eceef1',
                    mt: 3,
                    mb: 2,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  },
                  '& h1': { 
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 4,
                    color: '#3c8cff',
                  },
                  '& h2': { 
                    fontSize: '1.8rem',
                    color: '#eceef1',
                    borderBottom: '2px solid #3c8cff',
                    pb: 1,
                    mb: 3,
                  },
                  '& h3': { 
                    fontSize: '1.4rem',
                    color: '#eceef1',
                    mb: 2,
                  },
                  '& h4': { 
                    fontSize: '1.2rem',
                    color: '#eceef1',
                    mb: 2,
                  },
                  '& h5, & h6': { 
                    fontSize: '1.1rem',
                    color: '#eceef1',
                    mb: 1,
                  },
                  
                  // Body text
                  '& p, & div': {
                    color: '#eceef1 !important',
                    lineHeight: 1.7,
                    mb: 2,
                    fontSize: '1rem',
                  },
                  
                  // Custom classes from the HTML
                  '& [data-custom-class="body"]': {
                    color: '#eceef1 !important',
                    lineHeight: 1.7,
                  },
                  '& [data-custom-class="title"]': {
                    color: '#3c8cff',
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 2,
                  },
                  '& [data-custom-class="subtitle"]': {
                    color: '#8a8f98',
                    fontSize: '1rem',
                    textAlign: 'center',
                    mb: 4,
                  },
                  '& [data-custom-class="body_text"]': {
                    color: '#eceef1 !important',
                    lineHeight: 1.7,
                    mb: 2,
                  },
                  '& [data-custom-class="heading_1"]': {
                    color: '#eceef1',
                    fontSize: '1.8rem',
                    fontWeight: 600,
                    mb: 3,
                    mt: 4,
                  },
                  '& [data-custom-class="heading_2"]': {
                    color: '#eceef1',
                    fontSize: '1.4rem',
                    fontWeight: 600,
                    mb: 2,
                    mt: 3,
                  },
                  '& [data-custom-class="heading_3"]': {
                    color: '#eceef1',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    mb: 2,
                    mt: 2,
                  },
                  
                  // Lists
                  '& ul, & ol': {
                    color: '#b4b9c0',
                    lineHeight: 1.7,
                    mb: 2,
                    pl: 3,
                  },
                  '& li': {
                    mb: 1,
                    color: '#b4b9c0',
                  },
                  
                  // Emphasis
                  '& strong, & b': {
                    color: '#eceef1',
                    fontWeight: 600,
                  },
                  '& em, & i': {
                    color: '#b4b9c0',
                    fontStyle: 'italic',
                  },
                  
                  // Links
                  '& a': {
                    color: '#3c8cff',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: '#5ba0ff',
                    },
                  },
                  
                  // Tables
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    mb: 3,
                    backgroundColor: '#1a1d23',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid #40444b',
                  },
                  '& th, & td': {
                    border: '1px solid #40444b',
                    padding: 2,
                    textAlign: 'left',
                    verticalAlign: 'top',
                  },
                  '& th': {
                    backgroundColor: '#2a2d35',
                    color: '#eceef1',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                  },
                  '& td': {
                    color: '#b4b9c0',
                    fontSize: '0.9rem',
                  },
                  '& tr:nth-of-type(even)': {
                    backgroundColor: '#1e2127',
                  },
                  
                  // Special elements
                  '& .question': {
                    color: '#3c8cff',
                    fontWeight: 600,
                  },
                  '& .statement-end-if-in-editor': {
                    display: 'none',
                  },
                  '& .block-component': {
                    display: 'inline',
                  },
                  '& .noTranslate': {
                    color: '#eceef1',
                    fontWeight: 600,
                  },
                  
                  // Spacing and layout
                  '& br': {
                    display: 'block',
                    margin: '0.5em 0',
                    content: '""',
                  },
                  
                  // Highlight important sections
                  '& [data-custom-class="highlight"]': {
                    backgroundColor: '#2a2d35',
                    padding: 2,
                    borderRadius: 1,
                    border: '1px solid #40444b',
                    mb: 2,
                  },
                  
                  // Contact info styling
                  '& [data-custom-class="contact"]': {
                    backgroundColor: '#1a1d23',
                    padding: 3,
                    borderRadius: 2,
                    border: '1px solid #40444b',
                    mb: 3,
                  },
                  
                  // Horizontal rules
                  '& hr': {
                    border: 'none',
                    borderTop: '1px solid #40444b',
                    my: 3,
                  },
                }}
              >
                {/* Render HTML content directly */}
                <div
                  dangerouslySetInnerHTML={{
                    __html: content,
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default LegalDocument;

